import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";

/**
 * Global keyboard shortcut layer.
 *
 * Two flavors:
 *   - immediate single keys/chords (cmd+k, /, ?)
 *   - "g" prefix sequences (g h, g m, g t, g a)
 *
 * Anything that's typing into an <input>/<textarea>/contenteditable
 * gets a free pass — shortcuts don't fire while the user is typing.
 *
 * The "g" prefix uses a 1.2s window after pressing g; pressing the
 * second key within that window navigates. Outside the window, g is
 * a no-op.
 */

interface Options {
  onOpenPalette: () => void;
  onOpenCheatsheet: () => void;
}

export function useGlobalShortcuts({
  onOpenPalette,
  onOpenCheatsheet,
}: Options) {
  const navigate = useNavigate();

  useEffect(() => {
    let gPrefixUntil = 0;

    const isTyping = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return true;
      }
      if (target.isContentEditable) return true;
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K — palette (works even inside inputs).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenPalette();
        return;
      }

      // Ignore other shortcuts while typing.
      if (isTyping(e.target)) return;

      // Ignore if any modifier is held — those are reserved.
      if (e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) {
        // ? cheatsheet (which is shift+/) is the one exception.
        if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          onOpenCheatsheet();
        }
        return;
      }

      // /  → search
      if (e.key === "/") {
        e.preventDefault();
        navigate({ to: "/search" });
        return;
      }

      // g prefix
      if (e.key === "g") {
        gPrefixUntil = Date.now() + 1200;
        return;
      }

      // Second key within the g-prefix window?
      if (Date.now() < gPrefixUntil) {
        gPrefixUntil = 0;
        switch (e.key) {
          case "h":
            navigate({ to: "/" });
            break;
          case "m":
            navigate({ to: "/movies" });
            break;
          case "t":
            navigate({ to: "/tv" });
            break;
          case "a":
            navigate({ to: "/music" });
            break;
          case "s":
            navigate({ to: "/search" });
            break;
          case "i":
            navigate({ to: "/settings/indexers" });
            break;
        }
        return;
      }

      // ? cheatsheet without modifier (some keyboards)
      if (e.key === "?") {
        e.preventDefault();
        onOpenCheatsheet();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, onOpenPalette, onOpenCheatsheet]);
}
